import React from "react";
import styled, { keyframes } from "styled-components";

import * as u from "../utils";
import * as I from "./Icons";
import * as T from "./Typography";
import Button from "./Button";

export default function DropdownFilter(props) {
  const innerRefDesktop = React.useRef(null);
  const innerRefMobile = React.useRef(null);

  const [searchText, setSearchText] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const [isClosingAnimation, setIsClosingAnimation] = React.useState(false);

  React.useEffect(() => attachOutsideClick(), [isOpen]);

  return (
    <>
      <S.DesktopContainer
        ref={innerRefDesktop}
        data-cy={`${props.name}-dropdown-filter`}
      >
        {!isOpen && (
          <S.InitialButton onClick={() => setIsOpen(true)}>
            <S.Placeholder>
              <T.CM>{props.hint || ""}</T.CM>
              <div>
                <I.ArrowDown />
              </div>
            </S.Placeholder>
          </S.InitialButton>
        )}
        {isOpen && (
          <>
            <S.Input
              ref={(elm) => elm && elm.focus()}
              placeholder={"Type to search"}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <S.DesktopOptionsContainer
              isOpen={isOpen}
              isClosing={isClosingAnimation}
            >
              <S.DesktopHeaderWrapper>
                <S.Clear
                  onClick={() => {
                    props.onSelect([]);
                    setSearchText("");
                  }}
                >
                  Clear
                </S.Clear>
              </S.DesktopHeaderWrapper>
              {getOptions()}
            </S.DesktopOptionsContainer>
          </>
        )}
      </S.DesktopContainer>

      <S.MobileContainer>
        {!isOpen && (
          <S.ButtonWrapper>
            <Button
              onClick={() => setIsOpen(true)}
              buttonColour={u.colours.pillTeal}
              fontColour={u.colours.charcoal}
            >
              Filter
              <I.Dropdown
                style={{ marginLeft: 10 }}
                colour={u.colours.charcoal}
              />
            </Button>
          </S.ButtonWrapper>
        )}
        {isOpen && (
          <S.Background>
            <S.MobileFiltersContainer ref={innerRefMobile}>
              <S.MobileHeaderWrapper>
                <T.CXL>{props.hint || ""}</T.CXL>
                <S.Flex>
                  <S.Clear onClick={() => props.onSelect([])}>Clear</S.Clear>
                  <S.Close onClick={() => setIsOpen(false)}>
                    <I.Close />
                  </S.Close>
                </S.Flex>
              </S.MobileHeaderWrapper>
              <S.Filters>{getOptions()}</S.Filters>
              <S.ShowResultsContainer>
                <S.ButtonWrapper>
                  <Button
                    onClick={() => setIsOpen(false)}
                    text={"Show results"}
                  />
                </S.ButtonWrapper>
              </S.ShowResultsContainer>
            </S.MobileFiltersContainer>
          </S.Background>
        )}
      </S.MobileContainer>
    </>
  );

  function attachOutsideClick() {
    if (!isOpen) return;
    document.addEventListener("click", onOutsideClick);
    return () => document.removeEventListener("click", onOutsideClick);

    function onOutsideClick(evt) {
      if (innerRefDesktop?.current?.contains(evt.target)) return;
      if (innerRefMobile?.current?.contains(evt.target)) return;
      setSearchText("");
      setIsClosingAnimation(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosingAnimation(false);
      }, 300);
    }
  }

  function getOptions() {
    const term = searchText.toLowerCase();
    const options = { ...props.list };

    for (const [category, items] of Object.entries(options)) {
      options[category] = items.filter(byIncludedInSearch);
    }

    const allResultsEmpty =
      Object.entries(options).filter((category) => category[1].length > 0)
        .length === 0;

    if (allResultsEmpty) return noMatches();

    return Object.entries(options).map(getCategory);

    function byIncludedInSearch(elm) {
      const word = elm.toLowerCase();
      const isIncludedInSearch = word.includes(term);
      return !searchText || isIncludedInSearch;
    }
  }

  function noMatches() {
    return (
      <S.CategorySection>
        <S.Hint>Nothing matched your search</S.Hint>
      </S.CategorySection>
    );
  }

  function getCategory(category) {
    const [categoryTitle, categoryOptions] = category;

    const sentenceCaseTitle = categoryTitle
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    if (categoryOptions.length === 0) return;

    return (
      <>
        <S.CategorySection key={categoryTitle}>
          <S.CategoryTitle>{sentenceCaseTitle}</S.CategoryTitle>
          <S.PillsContainer>{categoryOptions.map(getOption)}</S.PillsContainer>
        </S.CategorySection>
        <S.Line />
      </>
    );
  }

  function getOption(option) {
    const isActive = props.value.length > 0 && props.value.includes(option);

    const onClick = () => {
      if (isActive) {
        props.onSelect(props.value.filter((e) => e !== option));
      } else {
        props.onSelect([...new Set([...props.value, option])]);
      }
      setSearchText("");
    };

    const subProps = {
      key: option,
      isActive,
      onClick,
    };

    return (
      <S.Pill {...subProps} data-cy={option + "-filter-pill"}>
        {option}
      </S.Pill>
    );
  }
}

const openAnimation = keyframes`
 0% { max-height: 0; padding: 0 0 10px 10px; }
 100% { max-height: 600px; padding: 0 0 0 10px; }
`;

const closeAnimation = keyframes`
0% { max-height: 600px; padding: 0 0 0 10px; }
100% { max-height: 0; padding: 0 0 10px 10px; }
`;

const S = {};

S.DesktopContainer = styled.div`
  display: none;
  @media ${u.device.mobile} {
    display: flex;
  }
  height: 40px;
  position: relative;
`;

S.MobileContainer = styled.div`
  display: block;
  @media ${u.device.mobile} {
    display: none;
  }
`;

S.InitialButton = styled.button`
  background: transparent;
  border: none;
`;

S.Placeholder = styled.div`
  align-items: center;
  color: ${u.colours.charcoal};
  font-family: "HK Grotesk", sans-serif;
  font-weight: 700;
  display: flex;
  outline: none;
  :hover {
    cursor: pointer;
  }
  padding: 10px;
  p {
    margin-right: 10px;
  }
`;

S.Input = styled.input`
  height: 40px;
  border: none;
  color: ${u.colours.charcoal};
  background: none;
  font-size: 1.5rem;
  outline: none;
  padding: 10px;
  width: 100%;
  ::placeholder {
    font-size: 1.5rem;
    font-weight: 700;
    opacity: 1;
  }
`;

S.ButtonWrapper = styled.div`
  position: fixed;
  bottom: 20px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  @media ${u.device.tablet} {
    display: none;
  }
`;

S.DesktopOptionsContainer = styled.ul`
  background-color: white;
  border-radius: 5px;
  box-shadow:
    rgba(0, 0, 0, 0.2) 0px 12px 28px 0px,
    rgba(0, 0, 0, 0.1) 0px 2px 4px 0px,
    rgba(255, 255, 255, 0.5) 0px 0px 0px 1px inset;
  height: fit-content;
  max-height: 600px;
  overflow-y: scroll;
  overflow-x: none;
  padding: 0 0 10px 10px;
  position: absolute;
  top: 40px;
  left: -320px;
  width: 640px;
  transition: max-height linear 0.3s;
  animation: ${(p) => (p.isClosing ? closeAnimation : openAnimation)} 0.4s;
  z-index: 999;
`;

S.DesktopHeaderWrapper = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 0 10px 0 0;
`;

S.Background = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(200, 200, 200, 0.9);
  align-items: center;
  justify-content: center;
  overflow: hidden;
  display: flex;
  z-index: 3;
`;

S.ButtonWrapper = styled.div`
  position: fixed;
  bottom: 15px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  @media ${u.device.tablet} {
    display: none;
  }
`;

S.MobileFiltersContainer = styled.div`
  position: fixed;
  height: 60vh;
  padding: 0 0 0 10px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background: white;
  z-index: 2;
  @media ${u.device.tablet} {
    display: none;
  }
`;

S.MobileHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${u.colours.shellOverlay};
  padding: 26px 16px;
`;

S.Flex = styled.div`
  display: flex;
`;

S.Clear = styled(T.CL)`
  cursor: pointer;
  padding: 6px 10px;
  text-decoration: underline;
  :hover {
    transform: scale(1.1);
  }
`;

S.Close = styled(S.Clear)`
  border-radius: 100%;
  height: 26px;
  margin-left: 13px;
  padding: 0;
  width: 26px;
`;

S.Filters = styled.div`
  background: white;
  padding: 16px;
  height: 100%;
  overflow-y: scroll;
`;

S.ShowResultsContainer = styled.div`
  background: white;
  height: 100%;
  width: 100%;
`;

S.CategorySection = styled.div`
  padding: 0 0 15px 0;
`;

S.CategoryTitle = styled(T.CL)`
  font-weight: 500;
  margin: 15px 0 15px 5px;
`;

S.PillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

S.Pill = styled.li`
  height: 35px;
  width: fit-content;
  align-items: center;
  background: ${u.colours.shellOverlay};
  border-radius: 50px;
  display: flex;
  margin: 0 5px 8px 0;
  padding: 0 15px;
  :hover {
    cursor: pointer;
    opacity: 0.8;
    transform: scale(1.05);
  }
  ${(p) =>
    p.isActive &&
    `
    background: ${u.colours.pillTeal};
    font-weight: 500;
  `}
`;

S.Line = styled.div`
  width: 100%;
  height: 1px;
  background: ${u.colours.shellOverlay};
`;

S.Hint = styled(S.CategoryTitle)`
  margin: 20px 0 0 10px;
  opacity: 0.6;
`;
